import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { AuthProvider } from "react-oidc-context";

// Polyfill for environments where crypto.subtle is not available
const ensureCryptoSubtle = () => {
  if (window.location.protocol !== "https:" && !window.crypto.subtle) {
    console.warn("Running in non-HTTPS context. Adding crypto polyfill for OIDC operations.");
    // Basic polyfill - for development only, not secure for production
    window.crypto.subtle = {
      digest: async () => {
        console.warn("Using insecure crypto polyfill - ONLY FOR DEVELOPMENT");
        // Return a dummy value
        return new Uint8Array(32).buffer;
      },
    };
  }
};

// Apply polyfill for development
ensureCryptoSubtle();

// Load runtime configuration
const getRuntimeConfig = () => {
  // Check if there's runtime config available with non-empty values
  const hasValidRuntimeConfig =
    window.APP_CONFIG &&
    Object.keys(window.APP_CONFIG).length > 0 &&
    window.APP_CONFIG.REACT_APP_OIDC_AUTHORITY; // Check for at least the critical value

  if (hasValidRuntimeConfig) {
    return window.APP_CONFIG;
  }

  // Fallback to environment variables (development mode)
  return {
    REACT_APP_OIDC_AUTHORITY: process.env.REACT_APP_OIDC_AUTHORITY,
    REACT_APP_OIDC_CLIENT_ID: process.env.REACT_APP_OIDC_CLIENT_ID,
    REACT_APP_OIDC_REDIRECT_URI: process.env.REACT_APP_OIDC_REDIRECT_URI,
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    REACT_APP_COMMIT_HASH: process.env.REACT_APP_COMMIT_HASH,
    REACT_APP_BUILD_DATE: process.env.REACT_APP_BUILD_DATE,
  };
};

const config = getRuntimeConfig();
console.log("App config loaded:", {
  authority: config.REACT_APP_OIDC_AUTHORITY,
  clientId: config.REACT_APP_OIDC_CLIENT_ID,
  redirectUri: config.REACT_APP_OIDC_REDIRECT_URI,
});

// Get the base URL dynamically
const getBaseUrl = () => {
  // In production use the actual host
  if (process.env.NODE_ENV === "production") {
    return window.location.origin;
  }
  // In development use the environment variable
  return config.REACT_APP_OIDC_REDIRECT_URI;
};

const oidcConfig = {
  authority: config.REACT_APP_OIDC_AUTHORITY,
  client_id: config.REACT_APP_OIDC_CLIENT_ID,
  redirect_uri: `${getBaseUrl()}/callback`, // Add a specific callback path
  post_logout_redirect_uri: getBaseUrl(),
  // The 'scope' determines what info you get back in the token
  scope: "openid profile email",
  // Browser-based OIDC clients must rely on PKCE and never ship a client secret.
  response_type: "code",
  audience: "cms-it-modules-mgmt", // Match the OIDC_AUDIENCE in backend

  // These settings are important for the authentication flow
  automaticSilentRenew: true,
  loadUserInfo: true,
  onSigninCallback: () => {
    // Remove the code from the URL to avoid issues with React Router
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <AuthProvider {...oidcConfig}>
      <App />
    </AuthProvider>
  </React.StrictMode>
);
